import type { IconNames } from '@dixa/lib-shared-habla';
import { v4 as uuid } from 'uuid';

import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import {
  ConditionOutcome,
  FlowNodeTypes,
} from '../../../powerFlowShared/components/types';
import { formatPortsHelper } from '../../../powerFlowShared/helpers/nodePortFormatter';
import { PowerEmailFlowExtraDataT } from '../PowerEmailFlow';
import { ContentModal as ActionModal } from '../modals/content';

export type FlowEmailNodeContentPayloadT = {
  rule_type: 'any' | 'all';
  rules: ContentRule[];
  match_node_id?: string | null;
  non_match_node_id?: string | null;
};

export type ContentRule = {
  type: 'subject' | 'from' | 'body';
  rule:
    | 'contains'
    | 'not-contains'
    | 'prefix'
    | 'suffix'
    | 'equals'
    | 'not-equals';
  match: string;
};
export type FlowEmailNodeContentTypeT = FlowNodeTypes.CONTENT;
export type FlowEmailNodeContentT = NodeT<
  FlowEmailNodeContentTypeT,
  FlowEmailNodeContentPayloadT
>;

const portTranslator = (node: FlowEmailNodeContentT): RegisterPortT[] => {
  return [
    {
      id: uuid(),
      type: 'out',
      label: ConditionOutcome.NO_MATCH,
      connection: node.payload.non_match_node_id || null,
    },
    {
      id: uuid(),
      type: 'out',
      label: ConditionOutcome.MATCH,
      connection: node.payload.match_node_id || null,
    },
  ];
};

const RULE_TYPES = {
  any: 'If any of the following conditions are met',
  all: 'If all of the following conditions are met',
};

export class ContentNode extends NodeTypeComponent<
  FlowEmailNodeContentT,
  PowerEmailFlowExtraDataT
>() {
  static icon: IconNames = 'align-left';
  static label = 'Content';
  static color = flowNodeColor.purple;
  static height = FlowNode.dimensions.h + 16;
  static ActionModal = ActionModal;
  static translatePayload = formatPortsHelper(portTranslator);
  static updateConnection = (
    node: FlowEmailNodeContentT,
    port: InternalPortT,
    newConnection: string,
  ): FlowEmailNodeContentT => {
    const payload = { ...node.payload };

    if (port.label === ConditionOutcome.MATCH) {
      payload.match_node_id = newConnection;
    }

    if (port.label === ConditionOutcome.NO_MATCH) {
      payload.non_match_node_id = newConnection;
    }

    return { ...node, payload };
  };

  triggerEdit = () => {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  };

  render() {
    const {
      node: {
        payload: { rule_type, rules },
      },
    } = this.props;

    return (
      <FlowNode
        color={ContentNode.color}
        icon={ContentNode.icon}
        label={ContentNode.label}
        text={
          <div style={{ height: '32px' }}>
            <span>{RULE_TYPES[rule_type]}</span>
            <br />
            <strong>
              {rules.length} {rules.length === 1 ? 'rule' : 'rules'}
            </strong>
          </div>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
